import React, { useState, useEffect } from 'react';

const IncidentClassification = ({
  catalogs,
  incidentData,
  onInputChange,
  getCategoriesByParent,
  getFormsByCategory,
  getFieldsSchema,
  errors
}) => {
  const { categories = [] } = catalogs || {};
  const safeIncidentData = incidentData || {};
  const safeErrors = errors || {};

  const [categoryLevels, setCategoryLevels] = useState([{ parentId: null, options: categories }]);
  const [selectedPath, setSelectedPath] = useState([]);
  const [availableForms, setAvailableForms] = useState([]);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [errorFields, setErrorFields] = useState(null);

  useEffect(() => {
    setCategoryLevels([{ parentId: null, options: categories }]);
  }, [categories]);

  useEffect(() => {
    const loadFormsForSelectedCategory = async () => {
      if (!safeIncidentData.category_id) {
        return;
      }
      try {
        setLoadingForms(true);
        const forms = await getFormsByCategory(safeIncidentData.category_id);
        setAvailableForms(forms);
      } catch (err) {
        console.error('Error al cargar formularios:', err);
      } finally {
        setLoadingForms(false);
      }
    };

    loadFormsForSelectedCategory();
  }, [safeIncidentData.category_id, getFormsByCategory]);

  useEffect(() => {
    if (!safeIncidentData.channel_id) {
      onInputChange('channel_id', 'web');
    }
  }, [safeIncidentData.channel_id, onInputChange]);

  const loadChildren = async (categoryId, levelIndex) => {
    const children = await getCategoriesByParent(categoryId);
    setCategoryLevels(prev => {
      const base = prev.slice(0, levelIndex + 1);
      if (children && children.length > 0) {
        return [...base, { parentId: categoryId, options: children }];
      }
      return base;
    });
    return children;
  };

  const handleCategoryLevelChange = async (levelIndex, value) => {
    const categoryId = value ? parseInt(value, 10) : null;
    const currentLevel = categoryLevels[levelIndex] || { options: [] };
    const selectedCategory = currentLevel.options.find(c => c.id === categoryId);

    const newPath = [...selectedPath.slice(0, levelIndex), selectedCategory].filter(Boolean);
    setSelectedPath(newPath);

    onInputChange('category_id', categoryId);
    onInputChange('category_name', newPath.map(c => c.name).join(' > '));
    onInputChange('form_id', null);
    setAvailableForms([]);

    if (!categoryId) {
      setCategoryLevels(prev => prev.slice(0, levelIndex + 1));
      return;
    }

    try {
      const children = await loadChildren(categoryId, levelIndex);
      if (!children || children.length === 0) {
        setLoadingForms(true);
        const forms = await getFormsByCategory(categoryId);
        setAvailableForms(forms);
      }
    } catch (err) {
      console.error('Error al cargar categorias/forms:', err);
      setAvailableForms([]);
    } finally {
      setLoadingForms(false);
    }
  };

  const handleFormChange = (e) => {
    const formId = e.target.value ? parseInt(e.target.value, 10) : null;
    const selectedForm = availableForms.find(f => f.id === formId);
    onInputChange('form_id', formId);
    onInputChange('form_name', selectedForm?.name || '');
  };

  useEffect(() => {
    const loadSchema = async () => {
      if (!safeIncidentData.category_id || !safeIncidentData.form_id) {
        onInputChange('fieldsSchema', null);
        onInputChange('loadingFields', false);
        onInputChange('errorFields', null);
        return;
      }

      setLoadingFields(true);
      setErrorFields(null);
      onInputChange('loadingFields', true);
      onInputChange('errorFields', null);

      try {
        const schema = await getFieldsSchema(safeIncidentData.category_id, safeIncidentData.form_id);
        onInputChange('fieldsSchema', schema);
      } catch (err) {
        const message = err.message || 'Error al cargar campos';
        setErrorFields(message);
        onInputChange('fieldsSchema', null);
        onInputChange('errorFields', message);
      } finally {
        setLoadingFields(false);
        onInputChange('loadingFields', false);
      }
    };

    loadSchema();
  }, [safeIncidentData.category_id, safeIncidentData.form_id, getFieldsSchema, onInputChange]);

  return (
    <div className="incident-classification">
      <div className="classification-grid">
        {categoryLevels.map((level, idx) => (
          <div className="form-group" key={`category_level_${idx}`}>
            <label htmlFor={`category_level_${idx}`}>Categoria nivel {idx + 1} *</label>
            <select
              id={`category_level_${idx}`}
              value={selectedPath[idx]?.id || ''}
              onChange={(e) => handleCategoryLevelChange(idx, e.target.value)}
              className={safeErrors.category_id ? 'error' : ''}
            >
              <option value="">Seleccione una categoria</option>
              {level.options.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        ))}

        <div className="form-group">
          <label htmlFor="form_id">Formulario *</label>
          <select
            id="form_id"
            value={safeIncidentData.form_id || ''}
            onChange={handleFormChange}
            className={safeErrors.form_id ? 'error' : ''}
            disabled={loadingForms || !safeIncidentData.category_id}
          >
            <option value="">
              {loadingForms ? 'Cargando formularios...' : 'Seleccione un formulario'}
            </option>
            {availableForms.map((form) => (
              <option key={form.id} value={form.id}>
                {form.name}
              </option>
            ))}
          </select>
          {safeErrors.form_id && <span className="error-message">{safeErrors.form_id}</span>}
        </div>
      </div>

      {loadingFields && <div className="loading-message">Cargando campos adicionales...</div>}
      {errorFields && <div className="error-message">Error al cargar campos: {errorFields}</div>}
    </div>
  );
};

export default IncidentClassification;
